/**
 * @file tpa-faq.component.ts
 */
import { Component, OnInit, Input } from '@angular/core';
import { BehaviorSubject, Observable, catchError, combineLatest, map, of, take, tap } from 'rxjs';
import { MatTabChangeEvent } from '@angular/material/tabs';

import { AnswerSet } from '@models/interfaces';
import { MultilingualService } from '@core/services';
import { AppCenterAnalytics, AppInsightsService, CmsService, MailOrderProfileEnrollmentService, UserService } from '@shared/services';
@Component({
    selector: 'app-tpa-faq',
    templateUrl: './tpa-faq.component.html',
    styleUrls: ['./tpa-faq.component.scss'],
    standalone: false
})
export class TpaFAQComponent implements OnInit {
  @Input() isOpenedFromGuest!: boolean;
  @Input() activeTabIndex!: number;

  benefitsFAQs$!: Observable<AnswerSet[]>;
  clinicalFAQs$!: Observable<AnswerSet[]>;
  mailOrdersFAQs$!: Observable<AnswerSet[]>;
  isBenefitsFAQsLoading = false;
  isClinicalFAQsLoading = false;
  isMailOrdersFAQsLoading = false;

  isBenefitsFAQsError = false;
  isClinicalFAQsError = false;
  isMailOrdersFAQsError = false;
  errMsg!: string;
  selectedIndex = 0;
  searchString!: string;
  noDataFound = false;

  highlightQuestionsSubject: BehaviorSubject<string> = new BehaviorSubject<string>('');

  constructor(
    public readonly cmsService: CmsService,
    private readonly appInsightsService: AppInsightsService,
    public readonly multilingualService: MultilingualService,
    public readonly mailOrderService: MailOrderProfileEnrollmentService,
    private readonly userService: UserService
  ) { }

  ngOnInit(): void {
    if (this.isOpenedFromGuest) {
      this.selectedIndex = this.activeTabIndex
    }
    this.getBenefitsFAQ();
    this.geClinicalReviewFAQ();

    const clientId = this.userService.eligibilityVerificationResponse?.clientId;
    this.mailOrderService
      .disableMailOrderFeaturesByClient(clientId)
      .pipe(take(1))
      .subscribe((disable) => {
        if (!disable) this.getMailOrderFAQ();
      });
  }

  getBenefitsFAQ(): void {
    this.isBenefitsFAQsLoading = true;
    this.isBenefitsFAQsError = false;
    this.benefitsFAQs$ = combineLatest([this.cmsService.getBenefitsFAQ(), this.highlightQuestionsSubject]).pipe(
      map(([FAQs, searchString]) => {
        if (searchString) {
          return FAQs.map((question) => {
            if (question.question.toLowerCase().includes(searchString.toLowerCase()) || question.answer.toLowerCase().includes(searchString.toLowerCase())) {
              question.highlightQuestion = true;
              if (question.answer.toLowerCase().includes(searchString.toLowerCase())) {
                question.isExpand = true;
              }
            } else {
              question.isExpand = false;
              question.highlightQuestion = false;
            }
            return question;
          });
        } else {
          return FAQs.map((question) => {
            question.highlightQuestion = false;
            question.isExpand = false;
            return question;
          });
        }
      }),
      tap((FAQs) => {
        if (this.selectedIndex === 0) {
          const value = FAQs.filter(x => x.highlightQuestion);
          if (value.length === 0) {
            this.appInsightsService.trackPageView(
              AppCenterAnalytics.BENEFITS_FAQ_SEARCH_NO_DATA_FOUND,
              { data: { value: this.searchString, itemCount: 0 } }
            );  
            this.noDataFound = true;
          } else {
            this.appInsightsService.trackPageView(AppCenterAnalytics.BENEFITS_FAQ_SEARCH_RESULT, {
              data: { value: this.searchString, itemCount: value.length },
            });  
            this.noDataFound = false;
          }
        }
        this.isBenefitsFAQsLoading = false;
      }),
      catchError((err) => {
        this.isBenefitsFAQsLoading = false;
        this.isBenefitsFAQsError = true;
        return of([]);
      }));
  }

  geClinicalReviewFAQ(): void {
    this.isClinicalFAQsLoading = true;
    this.isClinicalFAQsError = false;
    this.clinicalFAQs$ = combineLatest([this.cmsService.geClinicalReviewFAQ(), this.highlightQuestionsSubject]).pipe(
      map(([FAQs, searchString]) => {
        if (searchString) {
          return FAQs.map((question) => {
            if (question.question.toLowerCase().includes(searchString.toLowerCase()) || question.answer.toLowerCase().includes(searchString.toLowerCase())) {
              question.highlightQuestion = true;
              if (question.answer.toLowerCase().includes(searchString.toLowerCase())) {
                question.isExpand = true;
              }
            } else {
              question.highlightQuestion = false;
              question.isExpand = false;
            }
            return question;
          });
        } else {
          return FAQs.map((question) => {
            question.highlightQuestion = false;
            question.isExpand = false;
            return question;
          });
        }
      }),
      tap((FAQs) => {
        if (this.selectedIndex === 2) {
          const value = FAQs.filter(x => x.highlightQuestion);
          if (value.length === 0) {
            this.noDataFound = true;
            this.appInsightsService.trackPageView(
              AppCenterAnalytics.CLINICAL_REVIEW_FAQ_SEARCH_NO_DATA_FOUND,
              { data: { value: this.searchString, itemCount: 0 } }
            );
          } else {
            this.noDataFound = false;
            this.appInsightsService.trackPageView(
              AppCenterAnalytics.CLINICAL_REVIEW_FAQ_SEARCH_RESULT,
              { data: { value: this.searchString, itemCount: value.length } }
            );
          }
        }
        this.isClinicalFAQsLoading = false;
      }),
      catchError((err) => {
        this.isClinicalFAQsLoading = false;
        this.isClinicalFAQsError = true;
        this.errMsg = err;
        return of([]);
      }));
  }

  getMailOrderFAQ(): void {
    this.isMailOrdersFAQsLoading = true;
    this.isMailOrdersFAQsError = false;
    this.mailOrdersFAQs$ = combineLatest([this.cmsService.getMailOrderFAQ(), this.highlightQuestionsSubject]).pipe(
      map(([FAQs, searchString]) => {
        if (searchString) {
          return FAQs.map((question) => {
            if (question.question.toLowerCase().includes(searchString.toLowerCase()) || question.answer.toLowerCase().includes(searchString.toLowerCase())) {
              question.highlightQuestion = true;
              if (question.answer.toLowerCase().includes(searchString.toLowerCase())) {
                question.isExpand = true;
              }
            } else {
              question.highlightQuestion = false;
              question.isExpand = false;
            }
            return question;
          });
        } else {
          return FAQs.map((question) => {
            question.highlightQuestion = false;
            question.isExpand = false;
            return question;
          });
        }
      }),
      tap((FAQs) => {
        if (this.selectedIndex === 1) {
          const value = FAQs.filter(x => x.highlightQuestion);
          if (value.length === 0) {
            this.noDataFound = true;
            this.appInsightsService.trackPageView(
              AppCenterAnalytics.MAIL_ORDER_FAQ_SEARCH_NO_DATA_FOUND,
              { data: { value: this.searchString, itemCount: 0 } }
            );  
          } else {
            this.noDataFound = false;
            this.appInsightsService.trackPageView(AppCenterAnalytics.MAIL_ORDER_FAQ_SEARCH_RESULT, {
              data: { value: this.searchString, itemCount: value.length },
            });  
          }
        }
        this.isMailOrdersFAQsLoading = false;
      }),
      catchError((err) => {
        this.isMailOrdersFAQsError = true;
        this.isMailOrdersFAQsLoading = false;
        this.errMsg = err;
        return of([]);
      }));
  }

  search(searchString: string) {
    if (searchString?.length >= 3) {
      this.highlightQuestionsSubject.next(searchString);
    } else {
      this.highlightQuestionsSubject.next('');
    }
  }

  tabChange(event: MatTabChangeEvent) {
    this.selectedIndex = event.index;
    this.noDataFound = false;
    this.search(this.searchString);
  }

  onMailOrderFaqClicked(): void {
    this.appInsightsService.trackPageView(AppCenterAnalytics.MAIL_ORDER_FAQs_VIEWED);
  }

  onClinicalFaqClicked(): void {
    this.appInsightsService.trackPageView(AppCenterAnalytics.CLINICAL_REVIEW_FAQs_VIEWED);
  }

  onBenefitsFaqClicked(): void {
    this.appInsightsService.trackPageView(AppCenterAnalytics.BENEFITS_FAQs_VIEWED);
  }

}
